import { CellContext } from "@/lib/Components/DataTable/types";
import * as Headless from "@headlessui/react";
import { cn } from "@/lib/utils";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationStatuses } from "@/app/Relocations/GraphQL/relocationMutations";
import { useGetRelocationStatusMap } from "@/app/Dashboard/Utils/relocationStatusMap";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RelocationStatus } from "@/gql/graphql";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationEditDialog } from "@/app/Relocations/Components/RelocationEditDialog";
import { CreateWorkOrderDialog } from "@/app/ProDrivers/WorkOrders/Components/CreateWorkOrderDialog";
import { Link } from "@/components/catalyst/link";
import { CheckCircle } from "lucide-react";
import { useTranslations } from "use-intl";

export function RelocationStatusCell({
  cell,
  hideDriverRequest,
}: CellContext<RelocationListItem, any> & { hideDriverRequest?: boolean }) {
  const { mutateAsync, isPending } = useGqlMutation(updateRelocationStatuses);
  const { open } = useDialog(RelocationEditDialog);
  const { open: openWorkOrderDialog } = useDialog(CreateWorkOrderDialog);
  const t = useTranslations("relocation.table");

  const relocation = cell.row.original;
  const relocationStatusMap = useGetRelocationStatusMap();
  const { Icon, label, buttonColor, transitions } =
    relocationStatusMap[relocation.status];

  return (
    <div
      className="flex flex-col items-start gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            color={buttonColor}
            disabled={relocation.status === RelocationStatus.Archived}
          >
            {isPending ? (
              <Spinner data-slot="icon" />
            ) : (
              <Icon data-slot="icon" />
            )}
            <div className="whitespace-nowrap">{label}</div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {transitions.map((status) => {
            const { Icon, label, color } = relocationStatusMap[status];
            return (
              <DropdownMenuItem
                key={status}
                onClick={async () => {
                  if (
                    status === RelocationStatus.Ready &&
                    [
                      RelocationStatus.SoldOut,
                      RelocationStatus.Expired,
                      RelocationStatus.Paused,
                      RelocationStatus.Draft,
                    ].includes(relocation.status)
                  ) {
                    open({
                      id: relocation.id,
                      onSuccess: async () => {
                        await mutateAsync({
                          ids: [relocation.id],
                          status,
                        });
                      },
                    });

                    return;
                  }

                  await mutateAsync({
                    ids: [relocation.id],
                    status,
                  });
                }}
              >
                <div className="flex items-center space-x-2">
                  <Icon className={cn(`h-4 w-4`, color)} />
                  <div>{label}</div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {hideDriverRequest ? null : (relocation.workOrdersCount ?? 0) > 0 ? (
        <Link
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/pro-drivers"
          params={(prev) => ({ ...prev, relocationId: relocation.id })}
          className="flex items-center gap-1 whitespace-nowrap text-xs text-green-600 hover:text-green-700"
          onClick={(e) => e.stopPropagation()}
        >
          <CheckCircle className="h-3.5 w-3.5 shrink-0" />
          <span>{t("driverRequested")}</span>
        </Link>
      ) : (
        <Headless.Button
          className="select-none text-xs text-gray-500 hover:text-blue-600"
          onClick={(e) => {
            e.stopPropagation();
            const departureAddress = relocation.departureOffice.address;
            const deliveryAddress = relocation.deliveryOffice.address;
            openWorkOrderDialog({
              autoFocusNotes: true,
              initialValues: {
                supplier: { connect: relocation.supplier.id },
                vehicle_type: relocation.vehicle.type,
                currency: relocation.currency,
                relocation: { connect: relocation.id },
                depart_after: relocation.available_from_date,
                deliver_before: relocation.available_to_date,
                ...(departureAddress
                  ? {
                      departureAddress: {
                        upsert: {
                          address_line_1:
                            departureAddress.address_line_1 ?? undefined,
                          address_line_2:
                            departureAddress.address_line_2 ?? undefined,
                          city: departureAddress.city ?? undefined,
                          state: departureAddress.state ?? undefined,
                          country: departureAddress.country ?? undefined,
                          postcode: departureAddress.postcode ?? undefined,
                        },
                      },
                    }
                  : {}),
                ...(deliveryAddress
                  ? {
                      deliveryAddress: {
                        upsert: {
                          address_line_1:
                            deliveryAddress.address_line_1 ?? undefined,
                          address_line_2:
                            deliveryAddress.address_line_2 ?? undefined,
                          city: deliveryAddress.city ?? undefined,
                          state: deliveryAddress.state ?? undefined,
                          country: deliveryAddress.country ?? undefined,
                          postcode: deliveryAddress.postcode ?? undefined,
                        },
                      },
                    }
                  : {}),
              },
            });
          }}
        >
          {t("requestDriver")}
        </Headless.Button>
      )}
    </div>
  );
}
